/* Copyright (c) 2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import {
  useTable,
  useExpanded,
  useRowSelect,
  useSortBy,
  Cell,
} from "react-table";
import { Table } from "@nordcloud/gnui";
import {
  StyledTable,
  StyledRow,
  SVGIconDark,
  SVGIconLight,
  StyledTh,
  StyledTd,
} from "./ReactTableStyles";
import { TableProps, Column } from "./types";

// every react-table method generates 'key' prop
/* eslint-disable react/jsx-key */
function ReactTable({
  columns,
  data,
  striped,
  initialState = {},
  sort = false,
  hover,
  small,
  getRowProps = () => ({}),
  mb,
}: TableProps) {
  const expanded = React.useMemo(
    () => ({
      0: data.length === 1,
    }),
    [data]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        initialState: {
          sortBy: sort
            ? [
                {
                  id: "name",
                  desc: false,
                },
              ]
            : [],
          expanded,
          ...initialState,
        },
      },
      useSortBy,
      useExpanded,
      useRowSelect
    );

  if (rows?.length === 0) {
    return <p>No data available.</p>;
  }

  return (
    <StyledTable
      {...getTableProps()}
      striped={!!striped}
      hoverline={hover}
      small={small}
      mb={mb}
    >
      <Table.thead>
        {headerGroups.map((headerGroup) => (
          <Table.tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => {
              return (
                <StyledTh
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{
                    width: `${column.width}`,
                  }}
                >
                  {
                    <>
                      <span>{column.render("Header")}</span>
                      {column.canSort &&
                        column.isSorted &&
                        (column.isSortedDesc ? (
                          <SVGIconDark name="sortDown" size="sm" />
                        ) : (
                          <SVGIconDark name="sortUp" size="sm" />
                        ))}
                      {!column.canSort ||
                        (!column.isSorted && (
                          <SVGIconLight name="sort" size="sm" />
                        ))}
                    </>
                  }
                </StyledTh>
              );
            })}
          </Table.tr>
        ))}
      </Table.thead>
      <Table.tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);

          return (
            <StyledRow {...row.getRowProps(getRowProps(row))}>
              {row.cells.map((cell: Cell) => {
                const { hasRightBorder, hasLeftBorder } =
                  cell.column as unknown as Column;

                return (
                  <StyledTd
                    hasLeftBorder={hasLeftBorder}
                    hasRightBorder={hasRightBorder}
                    {...cell.getCellProps()}
                  >
                    {cell.render("Cell")}
                  </StyledTd>
                );
              })}
            </StyledRow>
          );
        })}
      </Table.tbody>
    </StyledTable>
  );
}

export default ReactTable;
